<div
  class="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-900 p-6"
>
  <div class="max-w-5xl mx-auto">
    <!-- En-tête -->
    <div class="mb-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white mb-2">
        Mon Profil
      </h1>
      <p class="text-gray-600 dark:text-gray-400">
        Gérez les informations de votre établissement et vos préférences.
      </p>
    </div>

    <!-- Loader -->
    @if (loading) {
      <div class="flex justify-center items-center py-20">
        <div
          class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"
        ></div>
      </div>
    }

    <!-- Formulaire -->
    @if (!loading) {
      <form [formGroup]="form" class="space-y-6">
        <!-- Informations générales -->
        <div
          class="bg-white dark:bg-slate-800 rounded-lg shadow-sm dark:shadow-slate-900/30 border border-gray-200 dark:border-slate-700 p-6"
        >
          <h2
            class="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2"
          >
            <span>🏪</span> Informations générales
          </h2>
          <div class="space-y-4">
            <!-- Nom de l'établissement -->
            <div>
              <label
                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Nom de l'établissement *
              </label>
              <input
                type="text"
                formControlName="businessName"
                class="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Ex: Le Bon Burger"
              />
              @if (
                form.get("businessName")?.invalid &&
                form.get("businessName")?.touched
              ) {
                <p class="mt-1 text-sm text-red-600 dark:text-red-400">
                  Le nom de l'établissement est requis
                </p>
              }
            </div>
            <!-- Type d'activité -->
            <div>
              <label
                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Type d'activité *
              </label>
              <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
                @for (activity of activityOptions; track activity) {
                  <button
                    type="button"
                    (click)="selectActivityType($any(activity.key))"
                    class="p-3 rounded-lg border text-sm font-medium transition-all"
                    [ngClass]="{
                      'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-500 text-indigo-900':
                        isFieldSelected('activityType', activity.key),
                      'bg-gray-50 dark:bg-slate-900 border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 hover:border-indigo-300':
                        !isFieldSelected('activityType', activity.key),
                    }"
                  >
                    {{ activity.label }}
                  </button>
                }
              </div>
            </div>
          </div>
        </div>
        <!-- Services et périodicité (Marchand fixe) -->
        @if (isFixedMerchant) {
          <div
            class="bg-white dark:bg-slate-800 rounded-lg shadow-sm dark:shadow-slate-900/30 border border-gray-200 dark:border-slate-700 p-6"
          >
            <h2
              class="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2"
            >
              <span>🍽️</span> Services proposés
            </h2>
            <div class="grid md:grid-cols-2 gap-6">
              <!-- Services -->
              <div>
                <label
                  class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Moments de service *
                </label>
                <div class="space-y-2">
                  @for (service of serviceOptions; track service) {
                    <button
                      type="button"
                      (click)="toggleService($any(service.key))"
                      class="w-full p-3 rounded-lg border text-sm font-medium transition-all text-left"
                      [ngClass]="{
                        'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-500 text-indigo-900':
                          isServiceSelected($any(service.key)),
                        'bg-gray-50 dark:bg-slate-900 border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 hover:border-indigo-300':
                          !isServiceSelected($any(service.key)),
                      }"
                    >
                      {{ service.label }}
                    </button>
                  }
                </div>
              </div>
              <!-- Périodicité -->
              <div>
                <label
                  class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Fréquence d'ouverture *
                </label>
                <div class="space-y-2">
                  @for (period of periodicityLabels | keyvalue; track period) {
                    <button
                      type="button"
                      (click)="selectPeriodicity($any(period.key))"
                      class="w-full p-3 rounded-lg border text-sm font-medium transition-all text-left"
                      [ngClass]="{
                        'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-500 text-indigo-900':
                          isFieldSelected('periodicity', period.key),
                        'bg-gray-50 dark:bg-slate-900 border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 hover:border-indigo-300':
                          !isFieldSelected('periodicity', period.key),
                      }"
                    >
                      {{ period.value }}
                    </button>
                  }
                </div>
              </div>
            </div>
          </div>
        }
        <!-- Rayon d'action (marchands itinérants uniquement) -->
        @if (isItinerantMerchant) {
          <div
            class="bg-white dark:bg-slate-800 rounded-lg shadow-sm dark:shadow-slate-900/30 border border-gray-200 dark:border-slate-700 p-6"
          >
            <h2
              class="text-xl font-bold text-gray-900 dark:text-white mb-1 flex items-center gap-2"
            >
              <span>🗺️</span> Rayon d'action
              <span
                class="ml-auto text-base font-bold text-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg px-3 py-1"
              >
                {{ form.value.actionRadius }} km
              </span>
            </h2>
            <p class="text-sm text-gray-400 mb-3">
              Distance max pour un événement.
            </p>
            <input
              type="range"
              formControlName="actionRadius"
              min="5"
              max="200"
              step="5"
              style="width: 100%; accent-color: #4f46e5"
            />
            <div class="flex justify-between text-xs text-gray-400 mt-1">
              <span>5 km</span><span>100 km</span><span>200 km</span>
            </div>
          </div>
        }
        <!-- Contexte opérationnel -->
        <div
          class="bg-white dark:bg-slate-800 rounded-lg shadow-sm dark:shadow-slate-900/30 border border-gray-200 dark:border-slate-700 p-6"
        >
          <h2
            class="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2"
          >
            <span>📊</span> Contexte opérationnel
          </h2>
          <div class="space-y-6">
            <!-- Affluence -->
            <div>
              <label
                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Affluence habituelle *
              </label>
              <div class="grid grid-cols-3 gap-3">
                @for (option of affluenceOptions; track option) {
                  <button
                    type="button"
                    (click)="selectAffluence($any(option.key))"
                    class="p-4 rounded-lg border transition-all text-center"
                    [ngClass]="{
                      'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-500 text-indigo-900':
                        isFieldSelected('affluence', option.key),
                      'bg-gray-50 dark:bg-slate-900 border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 hover:border-indigo-300':
                        !isFieldSelected('affluence', option.key),
                    }"
                  >
                    <div class="text-2xl mb-2">{{ option.icon }}</div>
                    <div class="text-sm font-semibold">{{ option.label }}</div>
                    <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {{ option.description }}
                    </div>
                  </button>
                }
              </div>
            </div>
            <!-- Taille équipe -->
            <div>
              <label
                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Taille de l'équipe *
              </label>
              <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
                @for (option of teamSizeOptions; track option) {
                  <button
                    type="button"
                    (click)="selectTeamSize($any(option.key))"
                    class="p-3 rounded-lg border transition-all text-center"
                    [ngClass]="{
                      'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-500 text-indigo-900':
                        isFieldSelected('teamSize', option.key),
                      'bg-gray-50 dark:bg-slate-900 border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 hover:border-indigo-300':
                        !isFieldSelected('teamSize', option.key),
                    }"
                  >
                    <div class="text-xl mb-1">{{ option.icon }}</div>
                    <div class="text-xs font-semibold">{{ option.label }}</div>
                  </button>
                }
              </div>
            </div>
            <!-- Couverture -->
            <div>
              <label
                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Couverture actuelle *
              </label>
              <div class="grid grid-cols-2 gap-3">
                @for (option of coverageOptions; track option) {
                  <button
                    type="button"
                    (click)="selectCoverage($any(option.id))"
                    class="p-3 rounded-lg border transition-all text-left"
                    [ngClass]="{
                      'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-500 text-indigo-900':
                        isFieldSelected('coverage', option.id),
                      'bg-gray-50 dark:bg-slate-900 border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 hover:border-indigo-300':
                        !isFieldSelected('coverage', option.id),
                    }"
                  >
                    <div class="font-semibold text-sm">{{ option.label }}</div>
                    <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {{ option.description }}
                    </div>
                  </button>
                }
              </div>
            </div>
          </div>
        </div>
        <!-- Abonnement -->
        <div
          class="bg-white dark:bg-slate-800 rounded-lg shadow-sm dark:shadow-slate-900/30 border border-gray-200 dark:border-slate-700 p-6"
        >
          <h2
            class="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2"
          >
            <span>💳</span> Abonnement
          </h2>
          <div
            class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
          >
            <div>
              <p class="text-sm text-gray-600 dark:text-gray-400">
                Gérez votre abonnement, changez de plan ou consultez vos
                factures.
              </p>
            </div>
            <a
              routerLink="/subscription"
              class="px-5 py-2.5 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              <span>⚙️</span> Gérer mon abonnement
            </a>
          </div>
        </div>

        <!-- Actions -->
        <div
          class="flex flex-col sm:flex-row gap-4 justify-between items-center bg-white dark:bg-slate-800 rounded-lg shadow-sm dark:shadow-slate-900/30 border border-gray-200 dark:border-slate-700 p-6"
        >
          <button
            type="button"
            (click)="goToProducts()"
            class="w-full sm:w-auto px-6 py-3 bg-white dark:bg-slate-800 border-2 border-indigo-600 text-indigo-600 rounded-lg font-semibold hover:bg-indigo-50 dark:bg-indigo-900/20 transition-colors flex items-center justify-center gap-2"
          >
            <span>📦</span> Gérer mes produits
          </button>
          <button
            type="button"
            (click)="onSave()"
            [disabled]="!form.dirty || saving"
            class="w-full sm:w-auto px-8 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            @if (!saving) {
              <span>💾</span>
            }
            @if (saving) {
              <span class="animate-spin">⏳</span>
            }
            {{ saving ? "Enregistrement..." : "Enregistrer les modifications" }}
          </button>
        </div>
      </form>
      <app-two-factor-card class="block mt-6" />
      <app-account-card class="block mt-6" />
    }
  </div>
</div>
